import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { ActivityEvents, Severity } from '../../incidentsActivityPanel/types';
import { IncidentType } from '../../../edr/incidentsSideMenu/types';

/**
 * @summary Type here the description of this component
 * @tag alert-details
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('alert-details')
export class AlertDetailsWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({ type: Object, attribute: true, reflect: true })
  alert;

  @property({ type: Object, attribute: true, reflect: true })
  group;

  @property({ type: Function, attribute: false, reflect: false })
  translateKillChainPhases;

  @property({ type: Function, attribute: false, reflect: false })
  translateEdrEventSeverity;

  @property({ type: Function, attribute: false, reflect: false })
  translateXdrSensors;

  @property({ type: Function, attribute: false, reflect: false })
  translateXdrGroupTitleByNodeType;

  @property({ type: Function, attribute: false, reflect: false })
  translateXdrEntityType;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    return html`
      <gz-text
        class="mb-2 description-text"
        fontSize="${FontSizes.PARAGRAPH_12}"
        .innerHTML="${this.alert.translatedDescription}"
      >
      </gz-text>

      <gz-labeled-content label="${translateFn('Severity')}">${this.renderSeverity()}</gz-labeled-content>
      <gz-labeled-content label="${translateFn('Sensor')}">${this.renderSensor()}</gz-labeled-content>
      <gz-labeled-content label="${translateFn('Detected on')}">${this.renderDetectedOn()}</gz-labeled-content>
      <gz-labeled-content label="${translateFn('Kill chain phase')}">${this.renderKillChainPhase()}</gz-labeled-content>
      ${this.renderEndpointIncidentNumber(this.alert)}

      <gz-text class="subtitle mt-2 mb-1" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.SEMIBOLD}"
        >${translateFn('Artifacts involved')}
      </gz-text>
      ${this.renderArtifactsInvolved()}
      ${this.renderInteractions()}
      ${this.renderResources()}

      <gz-button
        color="primary"
        type="outline"
        buttonheight="small"
        buttonfontsize="small"
        theme="light"
        class="view-more-details-button"
        @click="${this.handleViewMoreDetails}"
      >
        ${translateFn('View more details')}
      </gz-button>
    `;
  }

  private renderSeverity() {
    let color = '';
    switch (this.alert.severity) {
      case Severity.HIGH:
        color = 'bullet-danger';
        break;
      case Severity.MEDIUM:
        color = 'bullet-orange';
        break;
      case Severity.LOW:
        color = 'bullet-yellow';
        break;
      default:
        color = 'bullet-neutral';
        break;
    }

    return html`<span class="cell-label has-bullet ${color}"
      >${this.translateEdrEventSeverity(this.alert.severity)}</span
    >`;
  }

  private renderSensor() {
    const sensors = this.alert.sensors;
    return html`${sensors.map(sensor => this.translateXdrSensors(sensor)).join(', ')}`;
  }

  private renderDetectedOn() {
    const date = new Date(this.alert.lastUpdated);
    const formattedDate = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const formattedTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return html`${formattedDate}, ${formattedTime}`;
  }

  private renderKillChainPhase() {
    return html`${this.translateKillChainPhases(this.alert.tactic)}`;
  }

  private renderArtifactsInvolved() {
    const items: TemplateResult[] = [];
    for (const [type, nodes] of Object.entries(this.alert.nodesDetails)) {
      items.push(this.getNodeDetailComponent(nodes, type));
    }

    return html`${items}`;
  }

  private getNodeDetailComponent(nodes, type) {
    const hasManyNodes = nodes.length > 1;
    const label = hasManyNodes ? this.translateXdrGroupTitleByNodeType(type) : this.translateXdrEntityType(type);
    const value = hasManyNodes ? nodes.length : nodes[0].name;

    return html`
      <gz-labeled-content label="${label}">
        <gz-tooltip class="w-full" tooltipText="${value}" isEllipsis>
          <gz-button
            type="quiet"
            lowercase
            color="primary"
            theme="light"
            buttonheight="small"
            buttonfontsize="small"
            class="accent cell-label inline-text"
            style="cursor: pointer;--small-button-font-size: var(--body-size); width: auto; display: inline;"
            @click="${() => this.handleNodeClick(nodes, type)}"
            >${value}
          </gz-button>
        </gz-tooltip>
      </gz-labeled-content>
    `;
  }

  private renderEndpointIncidentNumber(alert) {
    if (!alert.incidents || alert.incidents.length === 0) {
      return nothing;
    }
    const isSingleIncidentCorrelated = alert.incidents.length === 1;
    const onIncidentNumberClick = isSingleIncidentCorrelated
      ? () => this.handleEndpointIncidentClick(alert.incidents[0])
      : () => this.handleViewMoreDetails();
    const incidentNumberInnerText = isSingleIncidentCorrelated
      ? `#${alert.incidents[0].number}`
      : `${alert.incidents.length}`;
    const incidentNumbers = html`
      <gz-button
        type="quiet"
        lowercase
        color="primary"
        theme="light"
        buttonheight="small"
        buttonfontsize="small"
        class="accent cell-label inline-text"
        style="cursor: pointer;--small-button-font-size: var(--body-size); width: auto;"
        @click="${onIncidentNumberClick}"
        >${incidentNumberInnerText}
      </gz-button>
    `;
    return html` <gz-labeled-content label="${translateFn('Incidents')}"> ${incidentNumbers} </gz-labeled-content> `;
  }

  private handleEndpointIncidentClick(incident) {
    const params: Object = {
      type: incident.type,
      _id: incident._id,
    };
    const isEdrIncident = incident.type === IncidentType.EDR;
    Application.lib.edrUtils.openIncidentInNewTab(params, isEdrIncident);
  }

  private handleNodeClick(nodes, type) {
    this.dispatchEvent(
      new CustomEvent(ActivityEvents.VIEW_NODE_DETAILS, { detail: { nodes, type, alert: this.alert } })
    );
  }

  private renderInteractions() {
    if (!this.alert.interactions || this.alert.interactions.length === 0) {
      return html``;
    }

    if (this.alert.interactions.length === 1) {
      const { source, destination } = this.alert.interactions[0];

      const encodedSource = source;
      const encodedDestination = destination;

      return html` <gz-labeled-content label="${translateFn('Interactions')}">
        <gz-tooltip
          class="label w-full d-inline-block"
          tooltipText="${translateFn('{source} with {destination}', {
            source: encodedSource,
            destination: encodedDestination,
          })}"
          isEllipsis
        >
          <gz-text fontSize="12" class="d-inline">${encodedSource}</gz-text>
          ${this.renderArrow(encodedDestination)}
          <gz-text fontSize="12" class="d-inline">${encodedDestination}</gz-text>
        </gz-tooltip>
      </gz-labeled-content>`;
    }

    return html` <gz-labeled-content label="${translateFn('Interactions')}">
      ${this.alert.interactions.length}
    </gz-labeled-content>`;
  }

  private renderArrow(destination) {
    if (!destination) {
      return;
    }

    return html`<gz-icon size="14" class="d-inline arrow-icon" color="secondary">
      <svg style="transform: rotate(90deg)" viewBox="0 0 16 16" slot="custom-svg" width="14" height="14">
        <g>
          <path
            d="M12.0006 6L8.99999 8.99981L9.70357 9.70339L11.5036 7.90335V18H12.5043V7.89505L14.3043 9.69509L15.0079 8.99152L12.0006 6Z"
          ></path>
        </g>
      </svg>
    </gz-icon>`;
  }

  private renderResources() {
    const transitions = this.alert.transitions.filter(transition => transition.hasOwnProperty('resources'));
    const resources = transitions.map(transition => transition.resources).flat();

    if (resources.length === 0) {
      return html``;
    }

    return html`<gz-labeled-content label="${translateFn('Resources')}">
      <gz-button
        type="quiet"
        lowercase
        color="primary"
        theme="light"
        buttonheight="small"
        buttonfontsize="small"
        class="accent cell-label inline-text"
        style="cursor: pointer;--small-button-font-size: var(--body-size); width: auto;"
        @click="${this.handleResourcesClick}"
      >${resources.length}
      </gz-button>
    </gz-labeled-content>
    `;
  }

  private handleResourcesClick() {
    this.dispatchEvent(new CustomEvent(ActivityEvents.VIEW_MORE_DETAILS, { detail: { alert: this.alert, group: this.group, scrollTo: 'resources' } }));
  }

  private handleViewMoreDetails() {
    this.dispatchEvent(
      new CustomEvent(ActivityEvents.VIEW_MORE_DETAILS, { detail: { alert: this.alert, group: this.group } })
    );
  }

  constructor() {
    super();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'alert-details': AlertDetailsWebComponent;
  }
}
